import {
  clearSymptomHackVideo,
  createWhatWork as createWhatWorkService,
  createSymptom,
  fetchPdHackCategories,
  deleteWhatWork as deleteWhatWorkService,
  fetchAllSymptoms,
  fetchHackSymptoms,
  fetchLatestSymptomCommentPostedOn,
  fetchLatestWhatWorkCreatedTime,
  fetchSymptom,
  fetchWhatWork,
  fetchWhatWorks,
  fetchWhatWorksCommentCount,
  fetchWhatWorksCommentCounts,
  updateWhatWork as updateWhatWorkService,
  updateSymptom,
  voteWhatWork,
} from '@core/services/pd-hacks/pd-hacks-service'
import { fetchYouTubeVideoPublishDates } from '@core/services/youtube/youtube-service'
import {
  getAuthenticatedUidOrThrow,
  loadCurrentUserProfile,
} from '@core/services/user/user-service'
import { extractYouTubeVideoId } from '@domain/features/youtube/youtube-url'
import { CoreError, CoreErrorCause } from '@domain/models/core-error'
import type {
  CreateHackInput,
  PdHackCategory,
  CreateWhatWorkInput,
  HackSymptom,
  PdSymptom,
  UpdateHackPatch,
  UpdateWhatWorkPatch,
  WhatWork,
} from '@domain/models/pd-hacks'
import type { UserProfile } from '@domain/models/user'

export interface HackWhatWork extends WhatWork {
  commentsCount: number
}

function isAdminUser(user: UserProfile): boolean {
  return user.admin === true || user.isAdmin === true
}

function toVideoSortDate(
  symptom: PdSymptom,
  publishedAt?: Date,
): Date {
  if (publishedAt && !Number.isNaN(publishedAt.getTime())) {
    return publishedAt
  }

  if (symptom.createdTime && !Number.isNaN(symptom.createdTime.getTime())) {
    return symptom.createdTime
  }

  return new Date(0)
}

function compareWhatWorksByVotesThenNewest(a: WhatWork, b: WhatWork): number {
  const aVotes = a.totalVotes ?? 0
  const bVotes = b.totalVotes ?? 0
  if (aVotes !== bVotes) {
    return bVotes - aVotes
  }

  const aCreated = a.createdTime?.getTime() ?? 0
  const bCreated = b.createdTime?.getTime() ?? 0
  return bCreated - aCreated
}

function compareHacksBySortDateDesc(a: HackSymptom, b: HackSymptom): number {
  return b.videoSortDate.getTime() - a.videoSortDate.getTime()
}

function toDateValue(date: Date | undefined): number {
  if (!date || Number.isNaN(date.getTime())) {
    return 0
  }
  return date.getTime()
}

function normalizePdHacksCategoryId(pdHacksCategoryId?: string): string | undefined {
  const normalized = pdHacksCategoryId?.trim()
  return normalized && normalized.length > 0 ? normalized : undefined
}

const MIN_ACTIVITY_SCAN_CANDIDATES = 12
const MAX_ACTIVITY_SCAN_CANDIDATES = 40
const ACTIVITY_SCAN_MULTIPLIER = 4

function resolveActivityScanCandidateCount(
  requestedCount: number,
  totalCount: number,
): number {
  const safeRequestedCount = Math.max(1, requestedCount)
  const scaledTarget = safeRequestedCount * ACTIVITY_SCAN_MULTIPLIER
  const targetCount = Math.max(MIN_ACTIVITY_SCAN_CANDIDATES, scaledTarget)
  return Math.min(totalCount, Math.min(MAX_ACTIVITY_SCAN_CANDIDATES, targetCount))
}

async function loadAdminUserOrThrow(action: string): Promise<UserProfile> {
  const uid = getAuthenticatedUidOrThrow()
  const currentUser = await loadCurrentUserProfile(uid)
  if (!isAdminUser(currentUser)) {
    throw new CoreError({
      cause: CoreErrorCause.PermissionDenied,
      context: `Current user is not allowed to ${action}.`,
    })
  }
  return currentUser
}

function assertCanManageWhatWork(
  user: UserProfile,
  whatWork: WhatWork,
  action: string,
): void {
  if (isAdminUser(user)) {
    return
  }

  if (user.uid && whatWork.addedById === user.uid) {
    return
  }

  throw new CoreError({
    cause: CoreErrorCause.PermissionDenied,
    context: `Current user is not allowed to ${action}.`,
  })
}

export class PdHacksFeature {
  private static async resolveHackSymptoms(
    symptoms: PdSymptom[],
  ): Promise<HackSymptom[]> {
    const videoBySymptom = symptoms
      .map((symptom) => ({
        symptom,
        videoId: extractYouTubeVideoId(symptom.michelsPdHacksVideoUrl),
      }))
      .filter(
        (
          item,
        ): item is {
          symptom: PdSymptom
          videoId: string
        } => Boolean(item.videoId),
      )

    const publishDateMap = await fetchYouTubeVideoPublishDates(
      videoBySymptom.map((item) => item.videoId),
    )

    const hacks = videoBySymptom.map(({ symptom, videoId }) => {
      const publishedAt = publishDateMap[videoId]
      return {
        ...symptom,
        videoId,
        videoPublishedAt: publishedAt,
        videoSortDate: toVideoSortDate(symptom, publishedAt),
      } satisfies HackSymptom
    })

    hacks.sort(compareHacksBySortDateDesc)
    return hacks
  }

  public static async getAllHacks(pdHacksCategoryId?: string): Promise<HackSymptom[]> {
    try {
      const normalizedCategoryId = normalizePdHacksCategoryId(pdHacksCategoryId)
      const symptoms = await fetchHackSymptoms(normalizedCategoryId)
      return await PdHacksFeature.resolveHackSymptoms(symptoms)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to fetch Michel PD hacks.',
        underlyingException: error,
      })
    }
  }

  public static async getPdHackCategories(
    activeOnly = true,
  ): Promise<PdHackCategory[]> {
    try {
      return await fetchPdHackCategories(activeOnly)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to fetch PD hacks categories.',
        underlyingException: error,
      })
    }
  }

  public static async getLatestHacks(count = 3): Promise<HackSymptom[]> {
    return PdHacksFeature.getLatestHacksByActivity(count)
  }

  public static async getLatestHacksByActivity(
    count = 3,
  ): Promise<HackSymptom[]> {
    try {
      const hacks = await PdHacksFeature.getAllHacks()
      const candidateCount = resolveActivityScanCandidateCount(count, hacks.length)
      const candidates = hacks.slice(0, candidateCount)
      const hacksWithReferenceDate = await Promise.all(
        candidates.map(async (hack) => {
          const [latestWhatWorkDate, latestCommentDate] = await Promise.all([
            fetchLatestWhatWorkCreatedTime(hack.uid),
            fetchLatestSymptomCommentPostedOn(hack.uid),
          ])

          const referenceDate = new Date(
            Math.max(
              toDateValue(hack.videoSortDate),
              toDateValue(latestWhatWorkDate),
              toDateValue(latestCommentDate),
            ),
          )

          return { hack, referenceDate }
        }),
      )

      hacksWithReferenceDate.sort((left, right) => {
        const referenceDelta =
          right.referenceDate.getTime() - left.referenceDate.getTime()
        if (referenceDelta !== 0) {
          return referenceDelta
        }
        return right.hack.videoSortDate.getTime() - left.hack.videoSortDate.getTime()
      })

      return hacksWithReferenceDate.slice(0, count).map((item) => item.hack)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to fetch latest Michel PD hacks by activity.',
        underlyingException: error,
      })
    }
  }

  public static async getHack(symptomId: string): Promise<HackSymptom> {
    try {
      const symptom = await fetchSymptom(symptomId)
      const hacks = await PdHacksFeature.resolveHackSymptoms([symptom])
      const hack = hacks[0]
      if (!hack) {
        throw new CoreError({
          cause: CoreErrorCause.DataNotFound,
          context: `Hack for symptom "${symptomId}" was not found.`,
        })
      }
      return hack
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to fetch hack "${symptomId}".`,
        underlyingException: error,
      })
    }
  }

  public static async getAllSymptomsForAdmin(): Promise<PdSymptom[]> {
    try {
      return await fetchAllSymptoms()
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to fetch symptoms for admin.',
        underlyingException: error,
      })
    }
  }

  public static async getSymptom(symptomId: string): Promise<PdSymptom> {
    try {
      return await fetchSymptom(symptomId)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to fetch symptom "${symptomId}".`,
        underlyingException: error,
      })
    }
  }

  public static async getHackWhatWorks(symptomId: string): Promise<WhatWork[]> {
    try {
      const items = await fetchWhatWorks(symptomId)
      return [...items].sort(compareWhatWorksByVotesThenNewest)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to fetch what works for symptom "${symptomId}".`,
        underlyingException: error,
      })
    }
  }

  public static async getHackWhatWork(
    symptomId: string,
    whatWorkId: string,
  ): Promise<WhatWork> {
    try {
      return await fetchWhatWork(symptomId, whatWorkId)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to fetch what works "${whatWorkId}".`,
        underlyingException: error,
      })
    }
  }

  public static async getHackWhatWorkCommentCount(
    symptomId: string,
    whatWorkId: string,
  ): Promise<number> {
    try {
      return await fetchWhatWorksCommentCount(symptomId, whatWorkId)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to fetch comments count for what works "${whatWorkId}".`,
        underlyingException: error,
      })
    }
  }

  public static async getHackWhatWorksWithCommentCounts(
    symptomId: string,
  ): Promise<HackWhatWork[]> {
    const whatWorks = await PdHacksFeature.getHackWhatWorks(symptomId)
    const counts = await fetchWhatWorksCommentCounts(
      symptomId,
      whatWorks.map((item) => item.uid),
    )

    return whatWorks.map((item) => ({
      ...item,
      commentsCount: counts[item.uid] ?? 0,
    }))
  }

  public static async voteHackWhatWork(
    symptomId: string,
    whatWorkId: string,
    isPositive: boolean,
  ): Promise<{ totalVotes: number; userVote: 'up' | 'down' | null }> {
    try {
      const uid = getAuthenticatedUidOrThrow()
      return await voteWhatWork(symptomId, whatWorkId, uid, isPositive)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to vote on what works "${whatWorkId}".`,
        underlyingException: error,
      })
    }
  }

  public static async createHackWhatWork(
    symptomId: string,
    input: CreateWhatWorkInput,
  ): Promise<string> {
    try {
      const uid = getAuthenticatedUidOrThrow()
      return await createWhatWorkService(symptomId, input, uid)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to create what works for symptom "${symptomId}".`,
        underlyingException: error,
      })
    }
  }

  public static async updateHackWhatWork(
    symptomId: string,
    whatWorkId: string,
    patch: UpdateWhatWorkPatch,
  ): Promise<void> {
    try {
      const uid = getAuthenticatedUidOrThrow()
      const currentUser = await loadCurrentUserProfile(uid)
      const whatWork = await fetchWhatWork(symptomId, whatWorkId)
      assertCanManageWhatWork(currentUser, whatWork, 'update this hack')
      await updateWhatWorkService(symptomId, whatWorkId, patch)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to update what works "${whatWorkId}".`,
        underlyingException: error,
      })
    }
  }

  public static async deleteHackWhatWork(
    symptomId: string,
    whatWorkId: string,
  ): Promise<void> {
    try {
      const uid = getAuthenticatedUidOrThrow()
      const currentUser = await loadCurrentUserProfile(uid)
      const whatWork = await fetchWhatWork(symptomId, whatWorkId)
      assertCanManageWhatWork(currentUser, whatWork, 'delete this hack')
      await deleteWhatWorkService(symptomId, whatWorkId)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to delete what works "${whatWorkId}".`,
        underlyingException: error,
      })
    }
  }

  public static async createHackForNewSymptom(
    input: CreateHackInput,
  ): Promise<string> {
    try {
      await loadAdminUserOrThrow('create hacks')
      return await createSymptom(input)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to create hack symptom.',
        underlyingException: error,
      })
    }
  }

  public static async updateHackForSymptom(
    symptomId: string,
    patch: UpdateHackPatch,
  ): Promise<void> {
    try {
      await loadAdminUserOrThrow('update hacks')
      await updateSymptom(symptomId, patch)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to update hack symptom "${symptomId}".`,
        underlyingException: error,
      })
    }
  }

  public static async deleteHackForSymptom(symptomId: string): Promise<void> {
    try {
      await loadAdminUserOrThrow('delete hacks')
      await clearSymptomHackVideo(symptomId)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to delete hack "${symptomId}".`,
        underlyingException: error,
      })
    }
  }
}

export async function getAllHacks(pdHacksCategoryId?: string): Promise<HackSymptom[]> {
  return PdHacksFeature.getAllHacks(pdHacksCategoryId)
}

export async function getPdHackCategories(activeOnly = true): Promise<PdHackCategory[]> {
  return PdHacksFeature.getPdHackCategories(activeOnly)
}

export async function getLatestHacks(count = 3): Promise<HackSymptom[]> {
  return PdHacksFeature.getLatestHacks(count)
}

export async function getLatestHacksByActivity(
  count = 3,
): Promise<HackSymptom[]> {
  return PdHacksFeature.getLatestHacksByActivity(count)
}

export async function getHack(symptomId: string): Promise<HackSymptom> {
  return PdHacksFeature.getHack(symptomId)
}

export async function getAllSymptomsForAdmin(): Promise<PdSymptom[]> {
  return PdHacksFeature.getAllSymptomsForAdmin()
}

export async function getSymptom(symptomId: string): Promise<PdSymptom> {
  return PdHacksFeature.getSymptom(symptomId)
}

export async function getHackWhatWorks(symptomId: string): Promise<WhatWork[]> {
  return PdHacksFeature.getHackWhatWorks(symptomId)
}

export async function getHackWhatWork(
  symptomId: string,
  whatWorkId: string,
): Promise<WhatWork> {
  return PdHacksFeature.getHackWhatWork(symptomId, whatWorkId)
}

export async function getHackWhatWorkCommentCount(
  symptomId: string,
  whatWorkId: string,
): Promise<number> {
  return PdHacksFeature.getHackWhatWorkCommentCount(symptomId, whatWorkId)
}

export async function getHackWhatWorksWithCommentCounts(
  symptomId: string,
): Promise<HackWhatWork[]> {
  return PdHacksFeature.getHackWhatWorksWithCommentCounts(symptomId)
}

export async function voteHackWhatWork(
  symptomId: string,
  whatWorkId: string,
  isPositive: boolean,
): Promise<{ totalVotes: number; userVote: 'up' | 'down' | null }> {
  return PdHacksFeature.voteHackWhatWork(symptomId, whatWorkId, isPositive)
}

export async function createHackWhatWork(
  symptomId: string,
  input: CreateWhatWorkInput,
): Promise<string> {
  return PdHacksFeature.createHackWhatWork(symptomId, input)
}

export async function updateHackWhatWork(
  symptomId: string,
  whatWorkId: string,
  patch: UpdateWhatWorkPatch,
): Promise<void> {
  return PdHacksFeature.updateHackWhatWork(symptomId, whatWorkId, patch)
}

export async function deleteHackWhatWork(
  symptomId: string,
  whatWorkId: string,
): Promise<void> {
  return PdHacksFeature.deleteHackWhatWork(symptomId, whatWorkId)
}

export async function createHackForNewSymptom(
  input: CreateHackInput,
): Promise<string> {
  return PdHacksFeature.createHackForNewSymptom(input)
}

export async function updateHackForSymptom(
  symptomId: string,
  patch: UpdateHackPatch,
): Promise<void> {
  return PdHacksFeature.updateHackForSymptom(symptomId, patch)
}

export async function deleteHackForSymptom(symptomId: string): Promise<void> {
  return PdHacksFeature.deleteHackForSymptom(symptomId)
}
